import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const productsByCategory: Record<string, string[]> = {
  licenses: ['Microsoft Office 365', 'Microsoft Windows', 'Microsoft Server', 'Microsoft Azure', 'Adobe Creative Cloud', 'Adobe Acrobat', '3CX Phone System', 'Kaspersky Endpoint Security', 'Kaspersky Small Office', 'Ekahau AI Pro', 'Ekahau Survey'],
  support: ['Microsoft Support', 'Adobe Support', '3CX Support', 'Kaspersky Support', 'Ekahau Support'],
  procurement: ['Material Industrial', 'Peças Petrolíferas', 'Peças Tecnológicas'],
  hosting: ['Web Hosting Standard', 'Web Hosting Premium', 'Web Hosting Enterprise'],
};

export default function QuotePage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    product_category: '',
    product_name: '',
    quantity: 1,
    details: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
  });
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/quotes`, form, { withCredentials: true });
      setSuccess(true);
      setForm({ product_category: '', product_name: '', quantity: 1, details: '', contact_email: '', contact_phone: '', company_name: '' });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error submitting quote');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'licenses', label: t('cat.licenses') },
    { value: 'support', label: t('cat.support') },
    { value: 'procurement', label: t('cat.procurement') },
    { value: 'hosting', label: t('cat.hosting') },
  ];

  const products = form.product_category ? (productsByCategory[form.product_category] || []) : [];

  const inputStyle: React.CSSProperties = { width: '100%', padding: '14px 16px', border: '1px solid var(--border)', fontSize: 15, outline: 'none', boxSizing: 'border-box', background: 'white' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' };

  if (success) {
    return (
      <>
        <Header />
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 72, background: 'var(--bg-section)' }}>
          <div style={{ textAlign: 'center', padding: 48 }}>
            <CheckCircle size={64} style={{ color: 'var(--primary)', marginBottom: 24 }} />
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 32, fontWeight: 700, marginBottom: 12 }}>{t('quote.success')}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 16, marginBottom: 32 }}>
              {t('contact.subtitle')}
            </p>
            <button onClick={() => setSuccess(false)} data-testid="new-quote-btn"
              style={{ padding: '14px 32px', background: 'var(--primary)', color: 'white', fontWeight: 600, border: 'none', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('portal.new_quote')}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{ minHeight: '100vh', paddingTop: 72, background: 'var(--bg-section)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '64px 24px' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: 'var(--secondary)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12 }}>MALUZ TECHNOLOGIES</p>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)', marginBottom: 8 }}>{t('quote.title')}</h1>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', marginBottom: 40 }}>{t('quote.subtitle')}</p>

          {error && (
            <div data-testid="quote-error" style={{ padding: '12px 16px', background: 'rgba(211,47,47,0.08)', border: '1px solid var(--secondary)', color: 'var(--secondary)', marginBottom: 20, fontSize: 14 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} data-testid="quote-form" style={{ background: 'white', border: '1px solid var(--border)', padding: '40px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={labelStyle}>{t('quote.category')}</label>
                <select
                  data-testid="quote-category-select"
                  value={form.product_category}
                  onChange={e => setForm({...form, product_category: e.target.value, product_name: ''})}
                  required
                  style={{...inputStyle, cursor: 'pointer'}}
                >
                  <option value="">{t('quote.select_category')}</option>
                  {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div>
                <label style={labelStyle}>{t('quote.product')}</label>
                <select
                  data-testid="quote-product-select"
                  value={form.product_name}
                  onChange={e => setForm({...form, product_name: e.target.value})}
                  required
                  style={{...inputStyle, cursor: 'pointer'}}
                >
                  <option value="">{t('quote.select_product')}</option>
                  {products.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div>
                <label style={labelStyle}>{t('quote.quantity')}</label>
                <input data-testid="quote-quantity-input" type="number" min={1} value={form.quantity} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 1})} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>{t('quote.company')}</label>
                <input data-testid="quote-company-input" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} style={inputStyle} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={labelStyle}>{t('quote.email')}</label>
                  <input data-testid="quote-email-input" type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t('quote.phone')}</label>
                  <input data-testid="quote-phone-input" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>{t('quote.details')}</label>
                <textarea data-testid="quote-details-input" value={form.details} onChange={e => setForm({...form, details: e.target.value})} rows={4} style={{...inputStyle, resize: 'vertical'}} />
              </div>

              <button type="submit" data-testid="quote-submit-btn" disabled={loading}
                style={{ padding: '16px', background: 'var(--primary)', color: 'white', fontWeight: 600, fontSize: 15, border: 'none', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: loading ? 0.7 : 1 }}>
                {loading ? '...' : t('quote.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}
